import React from 'react';
import { Alert, Box, Paper, Typography } from '@mui/material';
import ChatArtifactExportCard from './ChatArtifactExportCard';
import ChatArtifactFindLeadsButton from './ChatArtifactFindLeadsButton';
import ChatArtifactLeadSummary from './ChatArtifactLeadSummary';
import ChatArtifactTable from './ChatArtifactTable';
import ChatArtifactSaveSearch, { SAVE_SEARCH_LABEL } from './ChatArtifactSaveSearch';
import CoreResultSetSummary from './CoreResultSetSummary';

function exportAction(artifact) {
  return (artifact?.actions || []).find(
    (action) => action.key === 'export_csv' || action.kind === 'export'
  );
}

function findLeadsSampleAction(artifact) {
  return (artifact?.actions || []).find((action) => action.key === 'find_leads_sample') || null;
}

function countLabel(artifact) {
  const count = artifact?.totalCount;
  if (typeof count !== 'number') return 'Showing sample companies';
  const prefix = artifact?.eligibilityCounts?.explicit ? 'Eligible results' : 'Total results';
  return `${prefix}: ${count.toLocaleString()} ${count === 1 ? 'company' : 'companies'}`;
}

function eligibilityCountLabel(label, count) {
  return typeof count === 'number' ? `${label}: ${count.toLocaleString()}` : null;
}

function noticeMessage(notice) {
  return typeof notice === 'string' ? notice : notice?.message;
}

function noticeSeverity(notice) {
  return typeof notice === 'object' && notice?.severity ? notice.severity : 'info';
}

export default function ChatResultArtifact({
  artifact,
  isLatestList = false,
  isExporting = false,
  exportError = null,
  exportWarning = null,
  actionNotice = null,
  actionNoticeSeverity = 'success',
  actionError = null,
  workspace = null,
  actionInFlightKey = null,
  onRunAction,
  onRetryCoreResultSet,
}) {
  if (!artifact) return null;

  const hasRows = Array.isArray(artifact.rows) && artifact.rows.length > 0;
  const exportMetadata = exportAction(artifact);
  const runningAction = actionInFlightKey
    ? (artifact.actions || []).find((action) => action.key === actionInFlightKey)
    : null;
  const saveAction = (artifact.actions || []).find((action) => action.key === 'save_to_workspace');
  const saveControl = isLatestList && !workspace?.workspace_id && saveAction ? (
    <ChatArtifactSaveSearch
      action={saveAction}
      running={actionInFlightKey === 'save_to_workspace'}
      busy={Boolean(actionInFlightKey)}
      onRunAction={onRunAction}
    />
  ) : null;

  return (
    <Paper
      elevation={0}
      data-testid="chat-result-artifact"
      sx={{
        mt: 1.25,
        p: 1.75,
        width: 'min(100%, 860px)',
        maxWidth: '100%',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: '8px',
        bgcolor: 'background.paper',
      }}
    >
      <Box display="flex" flexDirection="column" gap={1.5}>
        {(artifact.title || artifact.coreResultSet || typeof artifact.totalCount === 'number') && (
          <Box>
            {artifact.title && (
              <Typography variant="subtitle1" sx={{ fontWeight: 700, letterSpacing: 0 }}>
                {artifact.title}
              </Typography>
            )}
            {artifact.coreResultSet ? (
              <CoreResultSetSummary
                model={artifact.coreResultSet}
                onRetry={onRetryCoreResultSet || (() => window.location.reload())}
              />
            ) : (
              <Typography variant="body2" color="text.secondary">
                {countLabel(artifact)}
              </Typography>
            )}
            {!artifact.coreResultSet && artifact.eligibilityCounts?.explicit && (
              <Box
                data-testid="chat-eligibility-counts"
                display="flex"
                flexWrap="wrap"
                columnGap={1.5}
                rowGap={0.25}
                sx={{ mt: 0.25 }}
              >
                {[
                  eligibilityCountLabel('Retrieved', artifact.eligibilityCounts.retrieved),
                  eligibilityCountLabel('Eligible', artifact.eligibilityCounts.eligible),
                  eligibilityCountLabel('Excluded', artifact.eligibilityCounts.excluded),
                ].filter(Boolean).map((label) => (
                  <Typography key={label} variant="caption" color="text.secondary">
                    {label}
                  </Typography>
                ))}
              </Box>
            )}
            {artifact.sourceDescription && (
              <Typography variant="caption" color="text.secondary">
                {artifact.sourceDescription}
              </Typography>
            )}
          </Box>
        )}

        {(artifact.notices || []).map((notice) => (
          <Alert
            key={typeof notice === 'object' ? notice.key : notice}
            severity={noticeSeverity(notice)}
            data-notice-code={typeof notice === 'object' ? notice.code || undefined : undefined}
            sx={{ py: 0.5, minWidth: 0, '& .MuiAlert-message': { overflowWrap: 'anywhere' } }}
          >
            {noticeMessage(notice)}
          </Alert>
        ))}

        {hasRows && (
          <ChatArtifactTable
            columns={artifact.columns}
            rows={artifact.rows}
            sampleLabel={artifact.sampleLabel}
          />
        )}

        <ChatArtifactLeadSummary summary={artifact.leadSummary} />

        {artifact.leadLockedHint && (
          <Typography variant="caption" color="text.secondary">
            {artifact.leadLockedHint}
          </Typography>
        )}

        {exportMetadata && (
          <ChatArtifactExportCard
            exportAction={exportMetadata}
            totalCount={artifact.totalCount}
            exportCount={artifact.exportCount}
            sampleCount={artifact.sampleCount}
            showPreviewCount={!artifact.coreResultSet}
            isExporting={isExporting}
            error={exportError}
            warning={exportWarning}
            onExport={() => onRunAction?.(exportMetadata.key)}
          >
            {saveControl}
          </ChatArtifactExportCard>
        )}

        {!exportMetadata && saveControl}

        <Box display="flex" flexWrap="wrap" alignItems="center" gap={1}>
          <ChatArtifactFindLeadsButton
            action={findLeadsSampleAction(artifact)}
            running={actionInFlightKey === 'find_leads_sample'}
            onRunAction={onRunAction}
          />
        </Box>

        {workspace?.workspace_id && (
          <Alert severity="success" sx={{ py: 0.5 }}>
            <strong>Saved</strong> as a Saved Search.{' '}
            <a href={`/workspaces/${workspace.workspace_id}`}>Open Saved Search</a>
          </Alert>
        )}

        {actionNotice && (
          <Alert severity={actionNoticeSeverity} sx={{ py: 0.5 }}>
            {actionNotice}
          </Alert>
        )}

        {runningAction && (
          <Alert severity="info" sx={{ py: 0.5 }}>
            {`${runningAction.key === 'save_to_workspace' ? SAVE_SEARCH_LABEL : runningAction.label} is running.`}
          </Alert>
        )}

        {actionError && (
          <Alert severity="error" sx={{ py: 0.5 }}>
            {actionError}
          </Alert>
        )}
      </Box>
    </Paper>
  );
}
